import { Injectable } from '@angular/core';
import { Notification, NotificationTypeEnum, PendencyTreatmentStatusEnum } from '@core/interfaces';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { sentry } from 'sentry';


@Injectable({
  providedIn: 'root'
})
export class NotificationsApi {

  constructor(private apollo: Apollo) { }

  async subscribe(pushSubscription: PushSubscription): Promise<string> {
    const CREATE_SUBSCRIPTION = gql`
      mutation createSubscription($input: SubscribeInput!) {
        subscribe(input: $input) {
          id
        }
      }
    `;

    const input = {
      type: 'browser',
      data: pushSubscription,
      app: 'mountdoom'
    };


    return this.apollo.use('mordu').mutate({
      mutation: CREATE_SUBSCRIPTION,
      variables: { input: input }
    }).toPromise()
      .then((result) => result.data['subscribe']['id'])
      .catch((err) => {
        sentry.captureException(err);
        return null;
      });
  }

  async unsubscribe(subscriptionId: string): Promise<void> {

    const UNSUBSCRIBE = gql`
      mutation unsubscribe($id: ID!) {
        unsubscribe(id: $id)
      }
    `;

    await this.apollo.use('mordu').mutate({
      mutation: UNSUBSCRIBE,
      variables: { id: subscriptionId }
    }).toPromise()
      .then((result) => result.data['unsubscribe'])
      .catch((err) => {
        sentry.captureException(err);
        return null;
      });
  }

  async listNotifications({ before, take }): Promise<Notification[]> {
    const LIST_NOTIFICATIONS = gql`
      query listNotifications($before: Datetime, $take: Int) {
        notifications(before: $before, take: $take) {
          id
          type
          data
          readAt
          createdAt
        }
      }
    `;

    return this.apollo.use('mordu')
      .query({
        query: LIST_NOTIFICATIONS,
        variables: {
          before,
          take
        }
      }).toPromise()
      .then((response) => {
        if (response.data && response.data['notifications']) {
          return response.data['notifications'];
        }

        return [];
      })
      .catch((err) => {
        sentry.captureException(err);
        return [];
      });
  }

  async getNotificationsUnreadCount(): Promise<number> {

    const GET_NOTIFICATIONS_UNREAD_COUNT = gql`
      query getNotificationsUnreadCount {
        notificationsUnreadCount {
          count
        }
      }
    `;

    return this.apollo.use('mordu')
      .query({
        query: GET_NOTIFICATIONS_UNREAD_COUNT,
      }).toPromise()
      .then((response) => response.data['notificationsUnreadCount']['count'])
      .catch((err) => {
        sentry.captureException(err);
        return null;
      });
  }

  async readNotification(id: string): Promise<Notification | null> {

    const READ_NOTIFICATION = gql`
      mutation notificationRead($id: ID!) {
        notificationRead(id: $id) {
          id
          readAt
        }
      }
    `;

    return this.apollo.use('mordu')
      .query({
        query: READ_NOTIFICATION,
        variables: {
          id
        }
      }).toPromise()
      .then((response) => {
        if (response.data && response.data['notificationRead']) {
          return response.data['notificationRead'];
        }

        return null;
      })
      .catch((err) => {
        sentry.captureException(err);
        return null;
      });
  }

  async readAllNotifications(): Promise<Number | null> {
    const READ_ALL_NOTIFICATIONS = gql`
      mutation notificationReadAll {
        notificationReadAll {
          count
        }
      }
    `;

    return this.apollo.use('mordu')
      .query({
        query: READ_ALL_NOTIFICATIONS,
      }).toPromise()
      .then(({ data }) => data['notificationReadAll']['count'])
      .catch((err) => {
        sentry.captureException(err);
        return null;
      });
  }

  async syncUser(): Promise<void> {
    const SYNC_USER = gql`
      mutation syncUser {
        syncUser {
          id
        }
      }
    `;

    await this.apollo.use('mordu')
      .mutate({ mutation: SYNC_USER })
      .toPromise()
      .catch((err) => sentry.captureException(err));
  }
}
